/* ============================================================================
   ZilZal — prices in the visitor's currency + Arabic currency symbols
   (Sep 2026; Oct 2026 #3: replaces the BUCKS currency app)

   Checkout is always charged in riyals (SAR, the shop currency). This file only
   changes how prices are DISPLAYED, exactly like BUCKS did.

   Oct 2026 #3 — BUCKS REMOVED (speed). BUCKS loaded a second jQuery, a 104 KB SDK,
   a 51 KB flag picture and an analytics call on every page, then re-wrote every
   price. It used two Shopify files for its work, and so does this file now:
     - /services/javascripts/currencies.js  Shopify's daily exchange rates (2.5 KB,
       same file and same rates BUCKS used). Fetched only by visitors viewing a
       non-SAR currency, kept in localStorage (zz_rates) for 12 h.
     - /browsing_context_suggestions.json  Shopify's guess of the visitor's country
       (same request BUCKS made). Fetched once at idle, kept 7 days (zz_cur_auto).
   Same 15 currencies, same auto-switch by country, no rounding (BUCKS settings as of
   Oct 2026). A manual pick (zz_cur) wins over the country guess. Returning BUCKS
   visitors keep their currency: their BUCKS choice is read once and copied over.
   The picker is drawn here into [data-zz-cur-list] (side menu: sections/zz-menu.liquid;
   PC header chip: assets/zz-site.js). Currency names come from the browser (Intl), so
   they are in the page language automatically.

   SYMBOLS (unchanged):
     SAR, AED  -> the official new symbols (SAMA / Central Bank of the UAE
                  artwork), drawn as a CSS mask so they take the price colour.
     IQD, KWD, BHD, ... -> the correct Arabic abbreviation, e.g. د.ع not ع.د.
     USD, EUR, TRY -> the formats BUCKS used ($1,234.56 / €1.234,56 / 1,234.56 TL).
   The symbol sits to the LEFT of the number (SAMA guideline), in its own
   right-to-left, bidi-isolated box.

   HOW: the original price element (always the shop's SAR text) is never edited. It
   is visually hidden, and a twin <span class="zz-px"> right after it shows the
   converted number + symbol. The twin never carries the .money class.
   [data-zzcart-sar] elements always stay in SAR (the "charged in SAR" figure).
   Cart drawer amounts carry their exact SAR value in cents (data-zz-sar-cents).

   Exposes window.zzFx = { parts(cents), currency(), rate(), set(code) }, sets
   <html data-zz-cur="KWD">, and fires a 'zz:fx' event when the currency or rate changes.

   Oct 2026 #4: the variant buttons on product pages (.bxp-vbtn__p) are converted like every
   other price (they used to stay in riyals, shown as "SR 0.30"), and zzFx.rate() (units of the
   visitor's currency per riyal) lets the Robux calculators (sections/zz-robux-starpets.liquid)
   read an amount typed in the visitor's own currency.

   Oct 2026 #5: country flags in the currency picker and on its button, so it reads as a currency
   switcher at a glance. 15 small SVGs (assets/zz-flag-<code>.svg, MIT flag-icons by Lipiridis),
   shown as CSS backgrounds: the list's flags download only when a picker is opened, the button
   only loads the active currency's flag. EUR shows the EU flag.

   TO REVERT: re-enable the BUCKS app embed (Theme settings > App embeds) and restore
   the previous version of this file, layout/theme.liquid, sections/zz-menu.liquid,
   assets/zz-site.js/.css (each change there is marked "Oct 2026 #3").
   ========================================================================== */
(function () {
  if (window.__zzPx) return;
  window.__zzPx = true;

  var SVG = {
    SAR: { label: 'ريال سعودي', cls: 'sar' },
    AED: { label: 'درهم إماراتي', cls: 'aed' }
  };
  /* Correct Arabic abbreviations, logical order (unit . country). */
  var TXT = {
    IQD: 'د.ع', KWD: 'د.ك', BHD: 'د.ب', JOD: 'د.أ', LYD: 'د.ل', TND: 'د.ت',
    DZD: 'د.ج', MAD: 'د.م', QAR: 'ر.ق', OMR: 'ر.ع', YER: 'ر.ي', EGP: 'ج.م',
    SDG: 'ج.س', LBP: 'ل.ل', SYP: 'ل.س', MRU: 'أ.م'
  };

  var TARGETS = '.money, [data-zzcart-sar], [data-bxp-now], [data-bxp-was], [data-zz-sar-cents], .bxp-vbtn__p';
  var CENTS = 'data-zz-sar-cents';
  var FORCE_SAR = '[data-zzcart-sar]';               /* the "charged in SAR" figure */
  var SAR_RX = /(^|[^A-Za-z])(SAR|SR)(?![A-Za-z])|ر\.س|\uFDFC/;
  var NUM_RX = /[0-9\u0660-\u0669](?:[0-9\u0660-\u0669.,\u066B\u066C]|\s(?=[0-9\u0660-\u0669]))*/;

  var css = ''
    + '.zz-px-src{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;white-space:nowrap!important;border:0!important;}'
    + '.zz-px{display:inline-flex!important;align-items:center;gap:.24em;direction:rtl;unicode-bidi:isolate;white-space:nowrap;}'
    + '.zz-px[hidden]{display:none!important;}'
    + '.zz-px__n{direction:ltr;unicode-bidi:isolate;}'
    + '.zz-px__sym{direction:rtl;unicode-bidi:isolate;}'
    + '.zz-px__t{unicode-bidi:isolate;}'
    + '.zz-px__svg{display:inline-block;flex:0 0 auto;height:.78em;background-color:currentColor;-webkit-mask:var(--zz-m) center/contain no-repeat;mask:var(--zz-m) center/contain no-repeat;}'
    + '.zz-px__svg--sar{width:.70em;--zz-m:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 1124.14 1256.39%27%3E%3Cpath d=%27M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z%27/%3E%3Cpath d=%27M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z%27/%3E%3C/svg%3E");}'
    + '.zz-px__svg--aed{width:.90em;--zz-m:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 1000 870%27%3E%3Cpath d=%27m88.3 1c0.4 0.6 2.6 3.3 4.7 5.9 15.3 18.2 26.8 47.8 33 85.1 4.1 24.5 4.3 32.2 4.3 125.6v87h-41.8c-38.2 0-42.6-0.2-50.1-1.7-11.8-2.5-24-9.2-32.2-17.8-6.5-6.9-6.3-7.3-5.9 13.6 0.5 17.3 0.7 19.2 3.2 28.6 4 14.9 9.5 26 17.8 35.9 11.3 13.6 22.8 21.2 39.2 26.3 3.5 1 10.9 1.4 37.1 1.6l32.7 0.5v43.3 43.4l-46.1-0.3-46.3-0.3-8-3.2c-9.5-3.8-13.8-6.6-23.1-14.9l-6.8-6.1 0.4 19.1c0.5 17.7 0.6 19.7 3.1 28.7 8.7 31.8 29.7 54.5 57.4 61.9 6.9 1.9 9.6 2 38.5 2.4l30.9 0.4v89.6c0 54.1-0.3 94-0.8 100.8-0.5 6.2-2.1 17.8-3.5 25.9-6.5 37.3-18.2 65.4-35 83.6l-3.4 3.7h169.1c101.1 0 176.7-0.4 187.8-0.9 19.5-1 63-5.3 72.8-7.4 3.1-0.6 8.9-1.5 12.7-2.1 8.1-1.2 21.5-4 40.8-8.9 27.2-6.8 52-15.3 76.3-26.1 7.6-3.4 29.4-14.5 35.2-18 3.1-1.8 6.8-4 8.2-4.7 3.9-2.1 10.4-6.3 19.9-13.1 4.7-3.4 9.4-6.7 10.4-7.4 4.2-2.8 18.7-14.9 25.3-21 25.1-23.1 46.1-48.8 62.4-76.3 2.3-4 5.3-9 6.6-11.1 3.3-5.6 16.9-33.6 18.2-37.8 0.6-1.9 1.4-3.9 1.8-4.3 2.6-3.4 17.6-50.6 19.4-60.9 0.6-3.3 0.9-3.8 3.4-4.3 1.6-0.3 24.9-0.3 51.8-0.1 53.8 0.4 53.8 0.4 65.7 5.9 6.7 3.1 8.7 4.5 16.1 11.2 9.7 8.7 8.8 10.1 8.2-11.7-0.4-12.8-0.9-20.7-1.8-23.9-3.4-12.3-4.2-14.9-7.2-21.1-9.8-21.4-26.2-36.7-47.2-44l-8.2-3-33.4-0.4-33.3-0.5 0.4-11.7c0.4-15.4 0.4-45.9-0.1-61.6l-0.4-12.6 44.6-0.2c38.2-0.2 45.3 0 49.5 1.1 12.6 3.5 21.1 8.3 31.5 17.8l5.8 5.4v-14.8c0-17.6-0.9-25.4-4.5-37-7.1-23.5-21.1-41-41.1-51.8-13-7-13.8-7.2-58.5-7.5-26.2-0.2-39.9-0.6-40.6-1.2-0.6-0.6-1.1-1.6-1.1-2.4 0-0.8-1.5-7.1-3.5-13.9-23.4-82.7-67.1-148.4-131-197.1-8.7-6.7-30-20.8-38.6-25.6-3.3-1.9-6.9-3.9-7.8-4.5-4.2-2.3-28.3-14.1-34.3-16.6-3.6-1.6-8.3-3.6-10.4-4.4-35.3-15.3-94.5-29.8-139.7-34.3-7.4-0.7-17.2-1.8-21.7-2.2-20.4-2.3-48.7-2.6-209.4-2.6-135.8 0-169.9 0.3-169.4 1zm330.7 43.3c33.8 2 54.6 4.6 78.9 10.5 74.2 17.6 126.4 54.8 164.3 117 3.5 5.8 18.3 36 20.5 42.1 10.5 28.3 15.6 45.1 20.1 67.3 1.1 5.4 2.6 12.6 3.3 16 0.7 3.3 1 6.4 0.7 6.7-0.5 0.4-100.9 0.6-223.3 0.5l-222.5-0.2-0.3-128.5c-0.1-70.6 0-129.3 0.3-130.4l0.4-1.9h71.1c39 0 78 0.4 86.5 0.9zm297.5 350.3c0.7 4.3 0.7 77.3 0 80.9l-0.6 2.7-227.5-0.2-227.4-0.3-0.2-42.4c-0.2-23.3 0-42.7 0.2-43.1 0.3-0.5 97.2-0.8 227.7-0.8h227.2zm-10.2 171.7c0.5 1.5-1.9 13.8-6.8 33.8-5.6 22.5-13.2 45.2-20.9 62-3.8 8.6-13.3 27.2-15.6 30.7-1.1 1.6-4.3 6.7-7.1 11.2-18 28.2-43.7 53.9-73 72.9-10.7 6.8-32.7 18.4-38.6 20.2-1.2 0.3-2.5 0.9-3 1.3-0.7 0.6-9.8 4-20.4 7.8-19.5 6.9-56.6 14.4-86.4 17.5-19.3 1.9-22.4 2-96.7 2h-76.9v-129.7-129.8l220.9-0.4c121.5-0.2 221.6-0.5 222.4-0.7 0.9-0.1 1.8 0.5 2.1 1.2z%27/%3E%3C/svg%3E");}';
  var st = document.createElement('style');
  st.setAttribute('data-zz-px', '');
  st.textContent = css;
  (document.head || document.documentElement).appendChild(st);

  var last = new WeakMap();   /* source element -> last rendered key */
  var twin = new WeakMap();   /* source element -> its twin */

  /* ---- currency state + rates (Oct 2026 #3, replaces BUCKS) ---- */
  var BASE = 'SAR';
  var LIST = ['SAR', 'AED', 'KWD', 'QAR', 'BHD', 'OMR', 'IQD', 'JOD', 'EGP', 'LYD', 'MAD', 'TND', 'TRY', 'USD', 'EUR'];
  /* country -> currency, only for currencies in LIST (anything else stays SAR, as with BUCKS) */
  var BY_COUNTRY = { SA: 'SAR', AE: 'AED', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR', IQ: 'IQD', JO: 'JOD',
    EG: 'EGP', LY: 'LYD', MA: 'MAD', EH: 'MAD', TN: 'TND', TR: 'TRY', US: 'USD' };
  'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK MC SM VA AD ME XK'.split(' ')
    .forEach(function (c) { BY_COUNTRY[c] = 'EUR'; });
  /* formats for currencies without an Arabic symbol (the formats BUCKS used) */
  var PLAIN = {
    USD: { tpl: '$\u0001', dec: '.', thou: ',' },
    EUR: { tpl: '€\u0001', dec: ',', thou: '.' },
    TRY: { tpl: '\u0001 TL', dec: '.', thou: ',' }
  };
  var RATES_URL = '/services/javascripts/currencies.js';
  var RATES_TTL = 12 * 36e5, AUTO_TTL = 7 * 864e5;

  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function valid(c) { return typeof c === 'string' && LIST.indexOf(c) > -1 ? c : null; }

  /* One-time copy of a returning visitor's BUCKS choice. */
  (function migrate() {
    if (lsGet('zz_cur_mig')) return;
    var m = lsGet('buckscc_last_manual'); m = valid(m && (m.currency || m));
    if (m && !valid(lsGet('zz_cur'))) lsSet('zz_cur', m);
    var a = lsGet('buckscc_auto_currency');
    if (a && valid(a.currency) && !lsGet('zz_cur_auto')) lsSet('zz_cur_auto', { c: a.currency, ts: a.timestamp || Date.now() });
    lsSet('zz_cur_mig', 1);
  })();

  var RATES = (function () { var r = lsGet('zz_rates'); return r && r.r && r.r.SAR ? r : null; })();
  function pickCurrency() {
    var m = valid(lsGet('zz_cur'));
    if (m) return m;
    var a = lsGet('zz_cur_auto');
    return (a && valid(a.c)) || BASE;
  }
  var CUR = pickCurrency();
  var html = document.documentElement;
  html.setAttribute('data-zz-cur', CUR);

  function activeCurrency() { return CUR; }

  /* rate + number format for the active currency; null = show SAR (SAR visitor, or rates not loaded) */
  var fxCache = { key: null, fx: null };
  function readFx() {
    var cur = CUR;
    if (cur === BASE || !RATES || !RATES.r[cur]) return null;
    var key = cur + '|' + RATES.ts;
    if (fxCache.key === key) return fxCache.fx;
    var p = PLAIN[cur] || { tpl: '\u0001', dec: '.', thou: ',' };
    fxCache.key = key;
    fxCache.fx = { rate: RATES.r.SAR / RATES.r[cur], dec: p.dec, thou: p.thou, decimals: 2, tpl: p.tpl };
    return fxCache.fx;
  }

  function parseRates(txt) {
    var out = {}, m, rx = /["']?\b([A-Z]{3})["']?\s*:\s*([0-9.]+(?:e[-+]?\d+)?)/g;
    var i = txt.indexOf('rates');
    if (i > -1) txt = txt.slice(i, txt.indexOf('}', i));
    while ((m = rx.exec(txt))) { var v = parseFloat(m[2]); if (v > 0) out[m[1]] = v; }
    return out.SAR ? out : null;
  }
  var loadingRates = false;
  function loadRates() {
    if (loadingRates || !window.fetch) return;
    loadingRates = true;
    fetch(RATES_URL, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) {
        var r = parseRates(t || '');
        if (!r) return;
        RATES = { ts: Date.now(), r: r };
        lsSet('zz_rates', RATES);
        if (CUR !== BASE) window.zzPxScan();
      })['catch'](function () {})
      .then(function () { loadingRates = false; });
  }
  function needRates() { if (CUR !== BASE && (!RATES || Date.now() - RATES.ts > RATES_TTL)) loadRates(); }

  function setCurrency(c, manual) {
    c = valid(c);
    if (!c) return;
    if (manual) lsSet('zz_cur', c);
    if (c === CUR) return;
    CUR = c;
    html.setAttribute('data-zz-cur', c);
    needRates();
    drawPickers();
    window.zzPxScan();
  }

  /* Country guess, once per 7 days, at idle — same request BUCKS made. */
  function detect() {
    if (valid(lsGet('zz_cur'))) return;
    var a = lsGet('zz_cur_auto');
    if (a && valid(a.c) && Date.now() - (a.ts || 0) < AUTO_TTL) return;
    if (!window.fetch) return;
    fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var cc = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        var c = (cc && BY_COUNTRY[String(cc).toUpperCase()]) || BASE;
        lsSet('zz_cur_auto', { c: c, ts: Date.now() });
        if (!valid(lsGet('zz_cur'))) setCurrency(c, false);
      })['catch'](function () {});
  }

  /* ---- picker ---- */
  var names = null;
  function nameOf(c) {
    try {
      if (!names) names = new Intl.DisplayNames([html.lang || 'ar'], { type: 'currency' });
      return names.of(c) || c;
    } catch (e) { return c; }
  }
  function drawPickers() {
    var lists = document.querySelectorAll('[data-zz-cur-list]');
    for (var i = 0; i < lists.length; i++) {
      var ul = lists[i];
      if (ul.getAttribute('data-zz-cur-drawn') !== CUR) {
        var h = '';
        for (var j = 0; j < LIST.length; j++) {
          var c = LIST[j], on = c === CUR;
          h += '<button type="button" class="zzm__loc-opt' + (on ? ' is-active' : '') + '" data-zz-cur-opt="' + c + '" aria-pressed="' + on + '">'
            + '<span class="zz-cur__opt"><i class="zz-flag zz-flag--' + c + '" aria-hidden="true"></i><span>' + escHtml(nameOf(c)) + ' <b class="zz-cur__code">' + c + '</b></span></span></button>';
        }
        ul.innerHTML = h;
        ul.setAttribute('data-zz-cur-drawn', CUR);
      }
    }
    var labels = document.querySelectorAll('[data-zz-cur-label]');
    for (var k = 0; k < labels.length; k++) {
      var t = labels[k].getAttribute('data-zz-cur-label') === 'name' ? nameOf(CUR) : CUR;
      if (labels[k].textContent !== t) labels[k].textContent = t;
    }
    /* Oct 2026 #5: flag of the active currency next to the button label (pictures: assets/zz-flag-*.svg, see zz-site.css) */
    var flags = document.querySelectorAll('[data-zz-cur-flag]');
    for (var g = 0; g < flags.length; g++) {
      var fc = 'zz-flag zz-flag--' + CUR;
      if (flags[g].className !== fc) flags[g].className = fc;
    }
    var fields = document.querySelectorAll('[data-zz-cur-field]');
    for (var f = 0; f < fields.length; f++) fields[f].classList.add('zz-cur-ready');
  }
  function closeAll(except) {
    var open = document.querySelectorAll('[data-zz-cur-field].is-open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] === except) continue;
      open[i].classList.remove('is-open');
      var b = open[i].querySelector('[data-zz-cur-toggle],[data-zzloc-toggle]');
      if (b) b.setAttribute('aria-expanded', 'false');
    }
  }
  document.addEventListener('click', function (e) {
    var opt = e.target.closest && e.target.closest('[data-zz-cur-opt]');
    if (opt) {
      e.preventDefault();
      var fld = opt.closest('[data-zz-cur-field]');
      setCurrency(opt.getAttribute('data-zz-cur-opt'), true);
      closeAll();
      var btn = fld && fld.querySelector('[data-zz-cur-toggle],[data-zzloc-toggle]');
      if (btn) btn.focus();
      return;
    }
    var tg = e.target.closest && e.target.closest('[data-zz-cur-toggle]');
    if (tg) {
      e.preventDefault();
      var field = tg.closest('[data-zz-cur-field]');
      if (!field) return;
      closeAll(field);
      var open = field.classList.toggle('is-open');
      tg.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    if (!(e.target.closest && e.target.closest('[data-zz-cur-field]'))) closeAll();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('[data-zz-cur-field].is-open');
    if (!open) return;
    closeAll();
    var b = open.querySelector('[data-zz-cur-toggle],[data-zzloc-toggle]');
    if (b) b.focus();
  });
  /* the PC header chip is added later by assets/zz-site.js */
  window.zzCurDraw = drawPickers;

  function ascii(s) {
    return s.replace(/[\u0660-\u0669]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
            .replace(/\u066B/g, '.').replace(/\u066C/g, ',');
  }

  function fmt(v, f) {
    var parts = v.toFixed(f.decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, f.thou);
    return parts[1] ? whole + f.dec + parts[1] : whole;
  }
  var SAR_FMT = { dec: '.', thou: ',', decimals: 2 };
  function escHtml(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  function drop(src) {
    var tw = twin.get(src);
    if (tw && tw.parentNode) tw.parentNode.removeChild(tw);
    twin.delete(src);
    last.delete(src);
    src.classList.remove('zz-px-src');
    src.removeAttribute('aria-hidden');
  }

  function render(src, cur) {
    var code, num, plain = null;
    if (src.hasAttribute(CENTS)) {
      /* Drawer amount: exact SAR value -> convert with the current rate. */
      var sar = (parseInt(src.getAttribute(CENTS), 10) || 0) / 100;
      var f = cur !== 'SAR' ? readFx() : null;
      if (f) {
        code = cur;
        num = fmt(sar * f.rate, f);
        if (!SVG[code] && !TXT[code]) plain = f.tpl.replace('\u0001', num);   /* USD, EUR, TRY */
      } else {
        code = 'SAR';
        num = fmt(sar, SAR_FMT);
      }
    } else {
      if (src.querySelector(TARGETS)) { drop(src); return; }    /* innermost only */
      var text = (src.textContent || '').trim();
      var m = text.match(NUM_RX);
      if (!m) { drop(src); return; }
      /* Oct 2026 #3: the source is always the shop's own SAR price (nothing else converts it now). */
      code = 'SAR';
      var ef = (cur !== 'SAR' && !src.matches(FORCE_SAR)) ? readFx() : null;
      if (ef) {
        var sv = parseFloat(ascii(m[0]).replace(/,/g, ''));
        if (sv >= 0) {
          code = cur;
          num = fmt(sv * ef.rate, ef);
          if (!SVG[code] && !TXT[code]) plain = ef.tpl.replace('\u0001', num);
        }
      }
      if (num === undefined) num = m[0].trim();                /* digits and separators only */
    }

    var key = code + '|' + num + '|' + (plain || '') + '|' + (src.hidden ? 1 : 0) + '|' + src.className;
    var tw = twin.get(src);
    if (tw && tw.isConnected && last.get(src) === key) return;
    last.set(src, key);

    if (!tw || !tw.isConnected) {
      tw = document.createElement('span');
      twin.set(src, tw);
      src.insertAdjacentElement('afterend', tw);
    }
    var keep = [];
    for (var i = 0; i < src.classList.length; i++) {
      var c = src.classList[i];
      if (c !== 'money' && c !== 'zz-px-src') keep.push(c);
    }
    tw.className = 'zz-px' + (keep.length ? ' ' + keep.join(' ') : '');
    tw.setAttribute('dir', 'rtl');
    tw.hidden = !!src.hidden;
    if (plain !== null) {
      tw.innerHTML = '<bdi class="zz-px__t">' + escHtml(plain) + '</bdi>';
    } else {
      var sym = SVG[code]
        ? '<span class="zz-px__svg zz-px__svg--' + SVG[code].cls + '" role="img" aria-label="' + SVG[code].label + '"></span>'
        : '<span class="zz-px__sym">' + TXT[code] + '</span>';
      tw.innerHTML = '<bdi class="zz-px__n" dir="ltr">' + num + '</bdi>' + sym;
    }
    src.classList.add('zz-px-src');
    src.setAttribute('aria-hidden', 'true');
  }

  /* Oct 2026: scans only what changed. It used to re-read every price on the whole page after ANY
     change anywhere (opening the cart, a ticker, an app widget), which cost 20 ms+ per frame on
     phones and made the cart drawer stutter. A full pass now runs only on load and when the
     visitor switches currency. */
  function handle(el, cur) {
    if (el.closest('.zz-px')) return;
    var owner = el.parentElement && el.parentElement.closest('[' + CENTS + ']');
    if (owner) return;                                          /* handled by its cents wrapper */
    render(el, cur);
  }
  function scanRoot(root, cur) {
    if (!root || !root.isConnected || root.nodeType !== 1) return;
    if (root.matches(TARGETS)) handle(root, cur);
    var list = root.querySelectorAll(TARGETS);
    for (var i = 0; i < list.length; i++) handle(list[i], cur);
  }
  /* A twin whose source was replaced (not edited) is orphaned: remove it. Twins sit right after
     their source, so only the children of a changed parent need checking. */
  function sweepTwins(parent) {
    if (!parent || !parent.isConnected) return;
    var kids = parent.children;
    for (var j = kids.length - 1; j >= 0; j--) {
      var tw = kids[j];
      if (!tw.classList || !tw.classList.contains('zz-px')) continue;
      var p = tw.previousElementSibling;
      if (!p || twin.get(p) !== tw) parent.removeChild(tw);
    }
  }
  var queued = false, full = true, roots = new Set(), parents = new Set(), lastCur = null;
  function scan() {
    queued = false;
    var cur = activeCurrency();
    if (full || cur !== lastCur) {
      full = false; lastCur = cur;
      roots.clear(); parents.clear();
      scanRoot(document.documentElement, cur);
      var tws = document.querySelectorAll('.zz-px');
      for (var j = 0; j < tws.length; j++) {
        var p = tws[j].previousElementSibling;
        if (!p || twin.get(p) !== tws[j]) tws[j].parentNode.removeChild(tws[j]);
      }
      announce();
      document.documentElement.classList.remove('zz-fx-wait');   /* layout/theme.liquid hid prices until now */
      return;
    }
    var rs = Array.from(roots), ps = Array.from(parents);
    roots.clear(); parents.clear();
    for (var i = 0; i < rs.length; i++) {
      var r = rs[i], skip = false;
      for (var k = 0; k < rs.length; k++) { if (k !== i && rs[k] !== r && rs[k].contains(r)) { skip = true; break; } }
      if (!skip) scanRoot(r, cur);
    }
    for (var m = 0; m < ps.length; m++) sweepTwins(ps[m]);
  }
  function queue() {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || setTimeout)(scan);
  }
  window.zzPxScan = function () { full = true; scan(); };

  /* Oct 2026 #2: money in the visitor's currency, split for UI that draws its own digits
     (the cart pill's rolling total). Same rate/format/symbol as every other price. */
  function parts(cents) {
    var sar = (Number(cents) || 0) / 100, cur = activeCurrency(), f = cur !== 'SAR' ? readFx() : null;
    var o = { code: 'SAR', num: '', pre: '', post: '', sym: '' };
    if (f) {
      o.code = cur; o.num = fmt(sar * f.rate, f);
      if (!SVG[cur] && !TXT[cur]) { var tp = f.tpl.split('\u0001'); o.pre = tp[0] || ''; o.post = tp[1] || ''; }
    } else o.num = fmt(sar, SAR_FMT);
    if (!o.pre && !o.post) {
      o.sym = SVG[o.code]
        ? '<span class="zz-px__svg zz-px__svg--' + SVG[o.code].cls + '" role="img" aria-label="' + SVG[o.code].label + '"></span>'
        : (TXT[o.code] ? '<span class="zz-px__sym">' + TXT[o.code] + '</span>' : '');
    }
    return o;
  }
  var fxSig = null;
  function announce() {
    var cur = activeCurrency(), f = cur !== 'SAR' ? readFx() : null;
    var sig = cur + '|' + (f ? f.rate + '|' + f.tpl : '');
    if (sig === fxSig) return;
    fxSig = sig;
    try { document.dispatchEvent(new CustomEvent('zz:fx')); } catch (e) {}
  }
  /* Oct 2026 #4: units of the visitor's currency per 1 riyal (1 for riyal visitors, or while the rates load). */
  function rate() { var f = activeCurrency() !== 'SAR' ? readFx() : null; return f ? f.rate : 1; }
  window.zzFx = { parts: parts, currency: activeCurrency, rate: rate };

  function rootFor(el) {
    if (!el || el.nodeType !== 1) return null;
    return el.closest(TARGETS) || el;
  }
  new MutationObserver(function (muts) {
    var any = false;
    for (var i = 0; i < muts.length; i++) {
      var mu = muts[i], t = mu.target;
      var el = t.nodeType === 1 ? t : t.parentElement;
      if (!el || (el.closest && el.closest('.zz-px'))) continue;   /* our own writes */
      /* Oct 2026: <head> never holds prices. Shopify's checkout pre-loader adds ~180 links there after
         load, and each one used to schedule a scan + an extra frame. */
      if (el === document.head || el.parentNode === document.head) continue;
      if (mu.type === 'attributes') {
        roots.add(rootFor(el)); any = true; continue;
      }
      if (mu.type === 'characterData') { roots.add(rootFor(el)); any = true; continue; }
      /* childList */
      var inPrice = el.closest(TARGETS);
      if (inPrice) { roots.add(inPrice); any = true; }
      for (var a = 0; a < mu.addedNodes.length; a++) {
        var n = mu.addedNodes[a];
        if (n.nodeType !== 1 || (n.classList && n.classList.contains('zz-px'))) continue;
        roots.add(n); any = true;
      }
      if (mu.removedNodes.length) {
        parents.add(el); any = true;
        /* Oct 2026 #3: a section re-render (Horizon morph) can drop our twins while keeping the price
           element itself. BUCKS used to re-write every price afterwards, which hid this; now the
           container is re-scanned so the twin comes back. */
        for (var rr = 0; rr < mu.removedNodes.length; rr++) {
          var rn = mu.removedNodes[rr];
          if (rn.nodeType === 1 && (rn.classList.contains('zz-px') || (rn.querySelector && rn.querySelector('.zz-px')))) { roots.add(el); break; }
        }
      }
    }
    if (any) queue();
  }).observe(document.documentElement, {
    subtree: true, childList: true, characterData: true,
    attributes: true, attributeFilter: ['hidden']
  });

  window.zzFx.set = function (c) { setCurrency(c, true); };

  function boot() { scan(); drawPickers(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  needRates();
  (window.requestIdleCallback || function (f) { setTimeout(f, 1500); })(detect, { timeout: 4000 });
})();
